import { USE_OPS_MOCKS } from 'pages/Operations/Shared/opsControl'
import { FUEL_DIESEL_PRICE_GHS } from 'pages/FuelManagement/fuelOpsDemoData'
import {
  IFuelConsumptionWindow,
  IFuelMachine,
  IFuelMachineDispense,
  IFuelMachineOperatorStat,
  IFuelMachineTaskStat,
} from 'types/fuelConsumption.interface'

export const FUEL_BAND_PCT = 15

export const FUEL_TELEMETRY_SILENCE_LIMIT_H = 6

export const FUEL_MACHINE_DIESEL_GHS = FUEL_DIESEL_PRICE_GHS

const round1 = (value: number): number => Math.round(value * 10) / 10

const win = (
  id: number,
  periodLabel: string,
  hours: number | null,
  litres: number,
  provenance: IFuelConsumptionWindow['provenance'],
  context: string,
  unusable?: boolean,
): IFuelConsumptionWindow => ({
  id,
  periodLabel,
  hours,
  litres,
  ratePerHour: unusable || hours == null || hours <= 0 ? null : round1(litres / hours),
  provenance,
  context,
  unusable,
})

const disp = (
  id: number,
  dateLabel: string,
  time: string,
  litres: number,
  smuHours: number | null,
  operator: string,
  station: string,
): IFuelMachineDispense => ({ id, dateLabel, time, litres, smuHours, operator, station })

const op = (
  id: number,
  name: string,
  windows: number,
  hours: number,
  litres: number,
  ratePerHour: number,
): IFuelMachineOperatorStat => ({ id, name, windows, hours, litres, ratePerHour })

const task = (
  id: number,
  context: string,
  hours: number,
  litres: number,
  sharePct: number,
): IFuelMachineTaskStat => ({ id, context, hours, litres, sharePct })

const DEMO_MACHINES: IFuelMachine[] = [
  {
    id: 9101,
    name: 'Truck 03',
    aliases: [],
    model: 'CAT 777 haul truck',
    machineClass: 'OPERATED_MOBILE',
    fuelDataMode: 'TELEMETRY',
    nominalBurnRateLph: 32.0,
    tankCapacityL: 1100,
    fuelLevelPct: 64,
    telemetrySilentSince: null,
    telemetrySilentHours: null,
    litres30d: 11400,
    avgRateLph: 33.9,
    litresPerTonne: 0.62,
    lastFillLabel: 'Today 06:55 · 190 L',
    trend7dLph: [33.6, 34.1, 33.8, 34.3, 33.5, 34.0, 33.9],
    note: null,
    flags: [],
    measuredSegments: [
      win(6, 'Today 06:05 – 09:30', 3.4, 115, 'MEASURED', 'Hauling — North Pit ore'),
      win(5, 'Yesterday 18:40 – 05:30', 10.8, 361, 'MEASURED', 'Night rotation — mixed haul'),
      win(4, 'Yesterday 13:00 – 18:00', 5.0, 172, 'MEASURED', 'Hauling — North Pit ore'),
      win(3, 'Yesterday 12:00 – 13:00', 1.0, 4.2, 'MEASURED', 'Idle — crusher queue · true idle burn'),
      win(2, 'Yesterday 06:10 – 12:00', 5.8, 198, 'MEASURED', 'Hauling — Waste Stripping'),
    ],
    fillWindows: [
      win(101, 'Yesterday → today 06:55', 22.4, 758, 'BETWEEN_FILLS', 'Fallback between-fills window — assumes fill-to-full'),
    ],
    dispenses: [
      disp(3, 'Today', '06:55', 190, 4233.0, 'K. Mensah', 'Tank A'),
      disp(2, 'Yesterday', '17:17', 160, 4221.8, 'K. Mensah', 'Tank A'),
      disp(1, 'Yesterday', '06:05', 220, 4210.6, 'K. Mensah', 'Tank A'),
    ],
    operators: [
      op(1, 'K. Mensah', 7, 58, 1984, 34.2),
      op(2, 'Peter M.', 6, 52, 1742, 33.5),
    ],
    tasks: [
      task(1, 'Hauling — North Pit ore · Plan: North Pit Extraction', 184, 6270, 55),
      task(2, 'Hauling — Waste Stripping', 100, 3420, 30),
      task(3, 'Roadworks support — Main Haul Road', 35, 1140, 10),
      task(4, 'Idle — crusher queue', 135, 570, 5),
    ],
    runtimeDays: [],
    unexplainedEvents: [],
    daysToEmpty: null,
    nextRefillNote: null,
    alerts: [
      { id: 1, severity: 'info', text: 'Refuel verified — 220 L dispensed, tank level rose 218 L', latency: 'live' },
    ],
  },
  {
    id: 9102,
    name: 'Truck 05',
    aliases: [],
    model: 'CAT 777 haul truck',
    machineClass: 'OPERATED_MOBILE',
    fuelDataMode: 'MANUAL',
    nominalBurnRateLph: 32.0,
    tankCapacityL: 450,
    fuelLevelPct: 18,
    telemetrySilentSince: null,
    telemetrySilentHours: null,
    litres30d: 9800,
    avgRateLph: 36.4,
    litresPerTonne: 0.71,
    lastFillLabel: 'Today 06:40 · 210 L',
    trend7dLph: [35.0, 34.6, 35.4, 35.1, 36.2, 35.8, 36.4],
    note: null,
    flags: [
      { kind: 'EXCESS_DISPENSE', label: 'EXCESS DISPENSE', tone: 'danger' },
      { kind: 'MISSING_SMU', label: 'MISSING SMU', tone: 'warning' },
    ],
    measuredSegments: [],
    fillWindows: [
      win(5, '2 Jul 15:40 → today 06:40', null, 210, 'BETWEEN_FILLS', 'Window unusable — SMU missing', true),
      win(4, '2 Jul 09:12 → 15:40', 5.7, 400, 'BETWEEN_FILLS', 'Second 400 L fill same shift — implausible vs 450 L tank'),
      win(3, '1 Jul → 2 Jul 09:12', 11.4, 400, 'BETWEEN_FILLS', 'Waste haulage — Pit B → South dump'),
      win(2, '30 Jun → 1 Jul', 11.0, 385, 'BETWEEN_FILLS', 'Waste haulage — Pit B → South dump'),
      win(1, '28 Jun → 30 Jun', null, 372, 'BETWEEN_FILLS', 'Window unusable — SMU missing', true),
    ],
    dispenses: [
      disp(6, 'Today', '06:40', 210, null, 'J. Owusu', 'Tank A'),
      disp(5, 'Yesterday', '15:40', 400, 6124.1, 'J. Owusu', 'Tank A'),
      disp(4, 'Yesterday', '09:12', 400, 6118.4, 'J. Owusu', 'Tank A'),
      disp(3, '1 Jul', '06:20', 385, 6107.0, 'J. Owusu', 'Tank A'),
      disp(2, '30 Jun', '06:25', 372, 6096.0, 'Michael K.', 'Tank A'),
      disp(1, '28 Jun', '06:20', 380, null, 'J. Owusu', 'Tank A'),
    ],
    operators: [
      op(1, 'J. Owusu', 4, 31, 1141, 36.8),
      op(2, 'Michael K.', 5, 44, 1536, 34.9),
    ],
    tasks: [
      task(1, 'Waste haulage — Pit B → South dump · Plan: Waste Stripping', 168, 6860, 70),
      task(2, 'Ore support — North Pit', 46, 1960, 20),
      task(3, 'Idle — dump queue', 58, 980, 10),
    ],
    runtimeDays: [],
    unexplainedEvents: [],
    daysToEmpty: null,
    nextRefillNote: null,
    alerts: [
      { id: 1, severity: 'danger', text: 'Excess dispense — two 400 L fills in one shift vs 450 L tank capacity', latency: 'next fill' },
      { id: 2, severity: 'warning', text: 'SMU missing on 2 dispenses — burn windows unusable until hours are logged', latency: 'next fill' },
      { id: 3, severity: 'warning', text: 'Fuel level 18% — refuel before night shift', latency: 'next fill' },
    ],
  },
  {
    id: 9103,
    name: 'Truck 12',
    aliases: [],
    model: 'CAT 777 haul truck',
    machineClass: 'OPERATED_MOBILE',
    fuelDataMode: 'MANUAL',
    nominalBurnRateLph: 32.0,
    tankCapacityL: 1100,
    fuelLevelPct: 71,
    telemetrySilentSince: null,
    telemetrySilentHours: null,
    litres30d: 10750,
    avgRateLph: 34.6,
    litresPerTonne: 0.68,
    lastFillLabel: 'Today 06:55 · 325 L',
    trend7dLph: [32.5, 32.8, 32.9, 38.4, 38.4, 39.1, 32.5],
    note: null,
    flags: [{ kind: 'OVER_BAND', label: 'OVER BAND', tone: 'danger' }],
    measuredSegments: [],
    fillWindows: [
      win(6, '2 Jul 18:40 → today 06:55', 10.0, 325, 'BETWEEN_FILLS', 'Post tyre change — night rotation, back in band'),
      win(5, '1 Jul → 2 Jul 06:10', 10.7, 418, 'BETWEEN_FILLS', 'Hauling — North Pit ore · day before tyre change'),
      win(4, '29 Jun → 1 Jul', 17.8, 684, 'BETWEEN_FILLS', 'Hauling — North Pit ore · rising burn before tyre change'),
      win(3, '28 Jun → 29 Jun', 11.2, 368, 'BETWEEN_FILLS', 'Hauling — North Pit ore'),
      win(2, '27 Jun → 28 Jun', 10.9, 358, 'BETWEEN_FILLS', 'Hauling — mixed ore + waste'),
      win(1, '26 Jun → 27 Jun', 10.6, 344, 'BETWEEN_FILLS', 'Hauling — North Pit ore'),
    ],
    dispenses: [
      disp(4, 'Today', '06:55', 325, 8880.6, 'Daniel R.', 'Tank A'),
      disp(3, '2 Jul', '06:10', 418, 8870.6, 'Daniel R.', 'Tank A'),
      disp(2, '1 Jul', '06:05', 684, 8859.9, 'Daniel R.', 'Tank A'),
      disp(1, '29 Jun', '06:00', 368, 8842.1, 'Mark O.', 'Tank A'),
    ],
    operators: [
      op(1, 'Daniel R.', 5, 41, 1480, 36.1),
      op(2, 'Mark O.', 6, 48, 1498, 31.2),
      op(3, 'K. Asante', 2, 9, 310, 34.4),
    ],
    tasks: [
      task(1, 'Hauling — North Pit ore · Plan: North Pit Extraction', 156, 5590, 52),
      task(2, 'Hauling — Waste Stripping', 92, 3440, 32),
      task(3, 'Roadworks support — Main Haul Road', 28, 1075, 10),
      task(4, 'Idle — queue + crib', 44, 645, 6),
    ],
    runtimeDays: [],
    unexplainedEvents: [],
    daysToEmpty: null,
    nextRefillNote: null,
    alerts: [
      { id: 1, severity: 'danger', text: 'Burn +20% over band for 2 consecutive windows — raise maintenance request', latency: 'next fill', maintenanceAction: true },
      { id: 2, severity: 'info', text: 'Back in band after tyre change — 32.5 L/h on the first post-repair window', latency: 'next fill' },
    ],
  },
  {
    id: 9104,
    name: 'GEN-01',
    aliases: ['Camp generator'],
    model: 'Camp generator · 500 kVA',
    machineClass: 'STATIC_PLANT',
    fuelDataMode: 'TELEMETRY',
    nominalBurnRateLph: 18.0,
    tankCapacityL: 950,
    fuelLevelPct: 78,
    telemetrySilentSince: null,
    telemetrySilentHours: null,
    litres30d: 7400,
    avgRateLph: 17.8,
    litresPerTonne: null,
    lastFillLabel: 'Today 07:05 · 302 L',
    trend7dLph: [17.7, 17.9, 17.8, 17.9, 17.7, 17.9, 17.8],
    note: null,
    flags: [{ kind: 'UNEXPLAINED', label: 'UNEXPLAINED', tone: 'danger' }],
    measuredSegments: [
      win(4, 'Today 05:30 – 09:30', 4.0, 72, 'MEASURED', 'Runtime — camp morning load'),
      win(3, 'Today 02:10 – 02:35', null, 40, 'MEASURED', 'Engine off — level drop · sent to fuel loss investigation'),
      win(2, 'Yesterday 22:00 – 02:10', 4.2, 66, 'MEASURED', 'Runtime — night low load'),
      win(1, 'Yesterday 06:00 – 22:00', 16.0, 285, 'MEASURED', 'Runtime — camp load'),
    ],
    fillWindows: [
      win(101, 'Yesterday 07:05 → today 07:05', 14.6, 302, 'BETWEEN_FILLS', 'Includes the unexplained 40 L — between-fills math cannot separate it'),
    ],
    dispenses: [
      disp(3, 'Today', '07:05', 302, 12494.8, 'S. Adjei', 'Tank B'),
      disp(2, 'Yesterday', '07:05', 255, 12480.2, 'S. Adjei', 'Tank B'),
      disp(1, '1 Jul', '07:00', 250, 12466.0, 'S. Adjei', 'Tank B'),
    ],
    operators: [],
    tasks: [],
    runtimeDays: [
      { id: 7, dateLabel: 'Today (to 09:30)', runtimeHours: 3.5, litres: 63 },
      { id: 6, dateLabel: '2 Jul', runtimeHours: 14.6, litres: 262 },
      { id: 5, dateLabel: '1 Jul', runtimeHours: 14.1, litres: 250 },
      { id: 4, dateLabel: '30 Jun', runtimeHours: 14.5, litres: 259 },
      { id: 3, dateLabel: '29 Jun', runtimeHours: 13.8, litres: 246 },
      { id: 2, dateLabel: '28 Jun', runtimeHours: 14.0, litres: 251 },
      { id: 1, dateLabel: '27 Jun', runtimeHours: 14.2, litres: 252 },
    ],
    unexplainedEvents: [
      { id: 1, dateLabel: 'Today', windowLabel: '02:10 – 02:35', litres: 40, note: 'Engine off — sent to fuel loss investigation' },
    ],
    daysToEmpty: 3,
    nextRefillNote: 'Next refill round — Thursday',
    alerts: [
      { id: 1, severity: 'danger', text: 'Unexplained 40 L drop, engine off — sent to fuel loss investigation', latency: 'live' },
      { id: 2, severity: 'info', text: 'Days to empty ~3 — scheduled for the Thursday refill round', latency: 'live' },
    ],
  },
  {
    id: 9105,
    name: 'EXC-01',
    aliases: ['CAT320'],
    model: 'CAT 320 excavator',
    machineClass: 'OPERATED_MOBILE',
    fuelDataMode: 'TELEMETRY',
    nominalBurnRateLph: 27.0,
    tankCapacityL: 700,
    fuelLevelPct: 58,
    telemetrySilentSince: null,
    telemetrySilentHours: null,
    litres30d: 18200,
    avgRateLph: 28.4,
    litresPerTonne: null,
    lastFillLabel: 'Today 06:15 · 620 L',
    trend7dLph: [28.2, 28.6, 28.3, 28.5, 28.1, 28.6, 28.4],
    note: null,
    flags: [],
    measuredSegments: [
      win(4, 'Today 06:15 – 09:30', 3.2, 92, 'MEASURED', 'Excavate bench — Pit B'),
      win(3, 'Yesterday 18:00 – 05:00', 10.4, 292, 'MEASURED', 'Excavate bench — Pit B · night'),
      win(2, 'Yesterday 17:30 – 18:00', 1.2, 6, 'MEASURED', 'Idle — truck wait · true idle burn'),
      win(1, 'Yesterday 06:10 – 17:30', 10.6, 305, 'MEASURED', 'Excavate bench — Pit B'),
    ],
    fillWindows: [
      win(101, 'Yesterday → today 06:15', 21.3, 620, 'BETWEEN_FILLS', 'Fallback between-fills window — assumes fill-to-full'),
    ],
    dispenses: [
      disp(2, 'Today', '06:15', 620, 9903.7, 'E. Boateng', 'Tank A'),
      disp(1, 'Yesterday', '06:10', 210, 9882.4, 'E. Boateng', 'Tank A'),
    ],
    operators: [
      op(1, 'E. Boateng', 6, 54, 1560, 28.9),
      op(2, 'Anna V.', 5, 49, 1370, 28.0),
    ],
    tasks: [
      task(1, 'Excavate bench — Pit B · Plan: Pit B Bench Development', 428, 12740, 70),
      task(2, 'Load waste — Pit B', 158, 4550, 25),
      task(3, 'Idle — truck wait', 62, 910, 5),
    ],
    runtimeDays: [],
    unexplainedEvents: [],
    daysToEmpty: null,
    nextRefillNote: null,
    alerts: [],
  },
  {
    id: 9106,
    name: 'DOZ-02',
    aliases: [],
    model: 'CAT D8 dozer',
    machineClass: 'OPERATED_MOBILE',
    fuelDataMode: 'MANUAL',
    nominalBurnRateLph: 24.0,
    tankCapacityL: 640,
    fuelLevelPct: 52,
    telemetrySilentSince: null,
    telemetrySilentHours: null,
    litres30d: 4900,
    avgRateLph: 19.6,
    litresPerTonne: null,
    lastFillLabel: 'Today 06:30 · 177 L',
    trend7dLph: [20.1, 19.8, 19.5, 19.9, 19.4, 19.7, 19.6],
    note: 'Burn well under band for the class — possible under-utilization, not a saving',
    flags: [],
    measuredSegments: [],
    fillWindows: [
      win(3, '2 Jul → today 06:30', 9.0, 177, 'BETWEEN_FILLS', 'Dump face + ramp maintenance'),
      win(2, '1 Jul → 2 Jul', 8.8, 171, 'BETWEEN_FILLS', 'Dump face + ramp maintenance'),
      win(1, '30 Jun → 1 Jul', 9.2, 182, 'BETWEEN_FILLS', 'Dump face + ramp maintenance'),
    ],
    dispenses: [
      disp(3, 'Today', '06:30', 177, 3230.4, 'Joseph A.', 'Tank A'),
      disp(2, '2 Jul', '06:25', 171, 3221.4, 'Joseph A.', 'Tank A'),
      disp(1, '1 Jul', '06:30', 182, 3212.6, 'Joseph A.', 'Tank A'),
    ],
    operators: [op(1, 'Joseph A.', 6, 46, 906, 19.7)],
    tasks: [
      task(1, 'Dump face + ramp maintenance · South dump', 212, 4165, 85),
      task(2, 'Stockpile dressing', 38, 735, 15),
    ],
    runtimeDays: [],
    unexplainedEvents: [],
    daysToEmpty: null,
    nextRefillNote: null,
    alerts: [],
  },
  {
    id: 9107,
    name: 'WT-01',
    aliases: [],
    model: 'Water truck · 20,000 L',
    machineClass: 'OPERATED_MOBILE',
    fuelDataMode: 'MANUAL',
    nominalBurnRateLph: 18.0,
    tankCapacityL: 400,
    fuelLevelPct: 61,
    telemetrySilentSince: null,
    telemetrySilentHours: null,
    litres30d: 5200,
    avgRateLph: 17.2,
    litresPerTonne: null,
    lastFillLabel: 'Today 06:50 · 171 L',
    trend7dLph: [17.0, 17.4, 17.1, 17.3, 17.0, 17.2, 17.3],
    note: null,
    flags: [],
    measuredSegments: [],
    fillWindows: [
      win(3, '2 Jul → today 06:50', 9.9, 171, 'BETWEEN_FILLS', 'Dust suppression — haul roads'),
      win(2, '1 Jul → 2 Jul', 10.1, 176, 'BETWEEN_FILLS', 'Dust suppression — haul roads'),
      win(1, '30 Jun → 1 Jul', 10.0, 170, 'BETWEEN_FILLS', 'Dust suppression + camp water'),
    ],
    dispenses: [
      disp(2, 'Today', '06:50', 171, 5541.8, 'S. Chirwa', 'Tank A'),
      disp(1, '2 Jul', '06:45', 176, 5531.9, 'S. Chirwa', 'Tank A'),
    ],
    operators: [op(1, 'S. Chirwa', 5, 38, 657, 17.3)],
    tasks: [
      task(1, 'Dust suppression — haul roads', 186, 4420, 85),
      task(2, 'Camp + workshop water', 28, 780, 15),
    ],
    runtimeDays: [],
    unexplainedEvents: [],
    daysToEmpty: null,
    nextRefillNote: null,
    alerts: [],
  },
  {
    id: 9108,
    name: 'CRUSH-01',
    aliases: [],
    model: 'Jaw crusher · primary',
    machineClass: 'STATIC_PLANT',
    fuelDataMode: 'MANUAL',
    nominalBurnRateLph: 42.0,
    tankCapacityL: 1200,
    fuelLevelPct: 55,
    telemetrySilentSince: null,
    telemetrySilentHours: null,
    litres30d: 9800,
    avgRateLph: 40.8,
    litresPerTonne: null,
    lastFillLabel: '2 Jul 16:05 · 334 L',
    trend7dLph: [41.0, 40.6, 41.2, 40.5, 40.9, 40.7, 40.8],
    note: null,
    flags: [],
    measuredSegments: [],
    fillWindows: [
      win(3, '2 Jul', 8.2, 334, 'BETWEEN_FILLS', 'Crushing shift — runtime log'),
      win(2, '1 Jul', 8.0, 328, 'BETWEEN_FILLS', 'Crushing shift — runtime log'),
      win(1, '30 Jun', 8.4, 342, 'BETWEEN_FILLS', 'Crushing shift — runtime log'),
    ],
    dispenses: [
      disp(2, '2 Jul', '16:05', 334, 18498.4, 'Plant crew', 'Plant tank'),
      disp(1, '1 Jul', '16:00', 328, 18490.2, 'Plant crew', 'Plant tank'),
    ],
    operators: [],
    tasks: [],
    runtimeDays: [
      { id: 7, dateLabel: 'Today (to 09:30)', runtimeHours: 2.5, litres: 102 },
      { id: 6, dateLabel: '2 Jul', runtimeHours: 8.2, litres: 334 },
      { id: 5, dateLabel: '1 Jul', runtimeHours: 8.0, litres: 328 },
      { id: 4, dateLabel: '30 Jun', runtimeHours: 8.4, litres: 342 },
      { id: 3, dateLabel: '29 Jun', runtimeHours: 7.8, litres: 320 },
      { id: 2, dateLabel: '28 Jun', runtimeHours: 8.0, litres: 326 },
      { id: 1, dateLabel: '27 Jun', runtimeHours: 8.2, litres: 336 },
    ],
    unexplainedEvents: [],
    daysToEmpty: 2,
    nextRefillNote: 'Next refill round — tomorrow 06:00',
    alerts: [],
  },
  {
    id: 9109,
    name: 'LV-07',
    aliases: [],
    model: 'Toyota LC79 light vehicle',
    machineClass: 'OPERATED_MOBILE',
    fuelDataMode: 'MANUAL',
    nominalBurnRateLph: 9.0,
    tankCapacityL: 130,
    fuelLevelPct: 44,
    telemetrySilentSince: null,
    telemetrySilentHours: null,
    litres30d: 780,
    avgRateLph: 8.6,
    litresPerTonne: null,
    lastFillLabel: 'Today 07:40 · 48 L',
    trend7dLph: [8.5, 8.7, 8.6, 8.8, 8.4, 8.6, 8.7],
    note: null,
    flags: [{ kind: 'MISSING_SMU', label: 'MISSING SMU', tone: 'warning' }],
    measuredSegments: [],
    fillWindows: [
      win(3, '2 Jul → today 07:40', null, 48, 'BETWEEN_FILLS', 'Window unusable — SMU missing', true),
      win(2, '28 Jun → 2 Jul', 6.0, 52, 'BETWEEN_FILLS', 'Site runs — general'),
      win(1, '26 Jun → 28 Jun', 6.1, 52, 'BETWEEN_FILLS', 'Site runs — general'),
    ],
    dispenses: [
      disp(3, 'Today', '07:40', 48, null, 'Pool drivers', 'Tank A'),
      disp(2, '2 Jul', '07:35', 52, 1285.8, 'Pool drivers', 'Tank A'),
      disp(1, '28 Jun', '07:30', 52, 1279.8, 'Pool drivers', 'Tank A'),
    ],
    operators: [op(1, 'Pool drivers', 2, 14, 121, 8.6)],
    tasks: [task(1, 'Site runs — general', 88, 780, 100)],
    runtimeDays: [],
    unexplainedEvents: [],
    daysToEmpty: null,
    nextRefillNote: null,
    alerts: [
      { id: 1, severity: 'warning', text: 'SMU missing on last dispense — burn window unusable', latency: 'next fill' },
    ],
  },
  {
    id: 9110,
    name: 'PUMP-03',
    aliases: [],
    model: 'Dewatering pump · Pit B sump',
    machineClass: 'STATIC_PLANT',
    fuelDataMode: 'MANUAL',
    nominalBurnRateLph: 6.5,
    tankCapacityL: 200,
    fuelLevelPct: 76,
    telemetrySilentSince: null,
    telemetrySilentHours: null,
    litres30d: 1150,
    avgRateLph: 6.3,
    litresPerTonne: null,
    lastFillLabel: 'Today 07:15 · 115 L',
    trend7dLph: [6.2, 6.4, 6.3, 6.2, 6.4, 6.3, 6.3],
    note: null,
    flags: [],
    measuredSegments: [],
    fillWindows: [
      win(2, '30 Jun → today 07:15', 18.2, 115, 'BETWEEN_FILLS', 'Dewatering — Pit B sump · runtime log'),
      win(1, '27 Jun → 30 Jun', 18.0, 114, 'BETWEEN_FILLS', 'Dewatering — Pit B sump · runtime log'),
    ],
    dispenses: [
      disp(2, 'Today', '07:15', 115, 7779.2, 'S. Adjei', 'Bowser 1'),
      disp(1, '30 Jun', '07:10', 114, 7761.0, 'S. Adjei', 'Bowser 1'),
    ],
    operators: [],
    tasks: [],
    runtimeDays: [
      { id: 4, dateLabel: '2 Jul', runtimeHours: 6.0, litres: 38 },
      { id: 3, dateLabel: '1 Jul', runtimeHours: 6.4, litres: 40 },
      { id: 2, dateLabel: '30 Jun', runtimeHours: 6.0, litres: 38 },
      { id: 1, dateLabel: '29 Jun', runtimeHours: 5.8, litres: 36 },
    ],
    unexplainedEvents: [],
    daysToEmpty: 4,
    nextRefillNote: 'Next refill round — Thursday',
    alerts: [],
  },
]

export const FUEL_CONSUMPTION_MACHINES: IFuelMachine[] = USE_OPS_MOCKS ? DEMO_MACHINES : []

export const findFuelMachineByName = (name: string): IFuelMachine | undefined =>
  FUEL_CONSUMPTION_MACHINES.find(
    (m) => m.name.toLowerCase() === (name || '').toLowerCase() ||
      m.aliases.some((a) => a.toLowerCase() === (name || '').toLowerCase()),
  )

export const isTelemetryDegraded = (machine: IFuelMachine): boolean =>
  machine.fuelDataMode === 'TELEMETRY' &&
  machine.telemetrySilentHours != null &&
  machine.telemetrySilentHours > FUEL_TELEMETRY_SILENCE_LIMIT_H

export const buildConsumptionSeries = (machine: IFuelMachine): IFuelConsumptionWindow[] => {
  if (machine.fuelDataMode === 'TELEMETRY' && !isTelemetryDegraded(machine)) {
    return machine.measuredSegments
  }
  return machine.fillWindows
}

export const avgProvenanceOf = (machine: IFuelMachine): IFuelConsumptionWindow['provenance'] =>
  machine.fuelDataMode === 'TELEMETRY' && !isTelemetryDegraded(machine) ? 'MEASURED' : 'BETWEEN_FILLS'

export const bandDeltaPct = (rate: number, nominal: number): number =>
  Math.round(((rate - nominal) / nominal) * 1000) / 10

export const bandStateOf = (rate: number, nominal: number): 'over' | 'in' | 'under' => {
  const delta = bandDeltaPct(rate, nominal)
  if (delta > FUEL_BAND_PCT) return 'over'
  if (delta < -FUEL_BAND_PCT) return 'under'
  return 'in'
}

export const bandLabelOf = (rate: number, nominal: number): string => {
  const state = bandStateOf(rate, nominal)
  if (state === 'over') return 'over band'
  if (state === 'under') return 'under band'
  return 'in band'
}

export const signedPctLabel = (delta: number): string => `${delta > 0 ? '+' : ''}${delta}%`

export const machineHeadlineRateLph = (machine: IFuelMachine): number => {
  if (machine.flags.some((f) => f.kind === 'OVER_BAND')) {
    const usable = buildConsumptionSeries(machine)
      .map((w) => w.ratePerHour)
      .filter((r): r is number => r != null)
    if (usable.length) return Math.max(...usable)
  }
  return machine.avgRateLph
}

export const worstFuelMachines = (machines: IFuelMachine[]): IFuelMachine[] => {
  const score = (machine: IFuelMachine): number => {
    const flagScore = machine.flags.reduce(
      (sum, f) => sum + (f.tone === 'danger' ? 200 : 100),
      0,
    )
    return flagScore + bandDeltaPct(machine.avgRateLph, machine.nominalBurnRateLph)
  }
  return [...machines].sort((a, b) => score(b) - score(a)).slice(0, 5)
}

export const machineFuelCost30dGhs = (machine: IFuelMachine): number =>
  Math.round(machine.litres30d * FUEL_MACHINE_DIESEL_GHS)

export const litresCostGhs = (litres: number): number => Math.round(litres * FUEL_MACHINE_DIESEL_GHS)

export const FUEL_FLEET_30D_L = FUEL_CONSUMPTION_MACHINES.reduce((sum, m) => sum + m.litres30d, 0)

export const FUEL_LEAGUE_SUBLINE = '30 days · all rates carry their source'

export const FUEL_LEAGUE_FOOTNOTE =
  'Tracked fleet 30d total ≈ 79,380 L against site burn ≈ 4,100 L/day — the remainder is the small-tool and light-plant long tail.'

export const FUEL_OPERATOR_FAIRNESS_CAPTION =
  'Differences are coaching signals, not discipline evidence — rates are attributed by operating time, not measured per operator.'

export const FUEL_TASK_FOOTNOTE = 'Feeds plan fuel cost in Operations → Control Room.'

export const operatorFairnessGuard = (stat: IFuelMachineOperatorStat): boolean =>
  stat.windows < 3 || stat.hours < 20

export const machineClassText = (machine: IFuelMachine): string =>
  machine.machineClass === 'OPERATED_MOBILE' ? '⛟ Mobile' : '⚙ Static plant'
